import React, { useMemo, useRef, useState } from 'react';
import { ArrowLeft, AlertTriangle, X, FolderOpen, CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import { Report, InvoiceConfig, QuoteConfig } from '../../types';
import { SigeEinweisung } from '../sige-plan/types';
import { buildCompanyForPdf, CompanyBase } from '../../services/pdf/companyForPdf';
import { buildEinweisungVorschlag } from '../../services/sigeEinweisung';
import {
  createEinweisung, markiereGebucht, setzeEinweisungLink, signedUrlFuerDokument,
} from '../../services/sigeEinweisungService';
import { describeError } from '../../services/errorMessage';
import { JournalMirrorRequest, queueJournalMirror } from '../../services/serviceJournal';
import { legeUnterlageAb, UnterlageAblegen } from '../../services/unterlagenAblage';
import { holdIdleExit } from '../../services/idleHold';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useRechte } from '../../hooks/useRechte';
import { EinweisungListe } from './EinweisungListe';
import { EinweisungEditor } from './EinweisungEditor';
import { PapierUploadDialog } from './PapierUploadDialog';
import { OnlineTeilnahmen } from './OnlineTeilnahmen';
import { baueEinweisungAblageDatei, exportiereEinweisungPdf, rechneEinweisungsBuchung } from './einweisungIntegration';
import {
  darfEinweisungAblegen,
  einweisungAblageBeschreibung,
  einweisungAblageStand,
  einweisungAblageTitel,
  einweisungAblageWarnung,
  einweisungFassungRef,
} from './einweisungAblage';
import { ladePlanQuelle, useEinweisungen } from './useEinweisungen';
import {
  darfBuchungAnbieten,
  darfNachbuchen,
  heuteIso,
  vereinigeTeilnehmer,
  vorbelegterDurchfuehrender,
  waehleEinweisungsPositionen,
} from './einweisungLogic';

/**
 * Die drei Ansichten des Moduls. `editor` und `online` brauchen eine
 * gewaehlte Einweisung; faellt sie aus der Ablage (Neuladen), landet man in
 * der Liste.
 */
type Ansicht = 'liste' | 'editor' | 'online';

/**
 * Was die Projektablage („Dokumente") zuletzt mit einem Blatt gemacht hat.
 * Der Abschluss bleibt gueltig, wenn die Ablage scheitert — dann steht hier
 * der Grund und der Knopf „Erneut ablegen".
 *
 * Im Fehlerfall wird der fertige AUFTRAG (Bytes + Kennzeichen) festgehalten
 * und „Erneut ablegen" schickt genau ihn. Das Blatt selbst ist nach dem
 * Abschluss eingefroren, aber die Online-Teilnahmen koennen bis zum erneuten
 * Versuch gewachsen sein — neu gesetzt waere es nicht mehr der Stand des
 * Abschlusses. `auftrag: null` nur, wenn schon das Setzen bzw. Holen der
 * Datei scheiterte; dann wird beim Wiederholen neu gebaut.
 */
type AblageStand =
  | { status: 'laeuft'; einweisung: SigeEinweisung }
  /** `warnung`: bei `schon_abgelegt` der Satz des Kerns zu einer verwaisten Datei — gelb, kein Retry. */
  | { status: 'abgelegt' | 'schon_abgelegt'; einweisung: SigeEinweisung; titel: string; warnung: string | null }
  | { status: 'fehler'; einweisung: SigeEinweisung; fehler: string; vonHand: boolean; auftrag: UnterlageAblegen | null };

interface Props {
  project: { id: string; name?: string; data: Report };
  userProfile: any;
  /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
  company?: CompanyBase;
  /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
  quoteConfig?: QuoteConfig;
  /** Rechnungs-Stammdaten fuer die Spalten-Fusszeile (App.invoiceConfigMemo). */
  invoiceConfig?: InvoiceConfig;
  onBack: () => void;
  /** Schreibt die verbuchte Leistung ins Projekt. Ohne ihn wird nicht gebucht. */
  onUpdate?: (report: Report) => void;
  isReadOnly?: boolean;
}

/**
 * Die Einweisung in den SiGe-Plan — duenner Container.
 *
 * Er laedt die Ablage, schaltet zwischen Liste und Editor und haelt den einen
 * Vorgang, der beide Wege verbindet: die Abrechnungsfrage. Jede Regel, die man
 * pruefen kann, steht daneben in einweisungLogic.ts (stehende Entscheidung
 * „duenner Container, Logik in Hooks + pure Funktionen"; es gibt kein jsdom,
 * Komponenten sind nicht testbar).
 *
 * ═══ DER RIEGEL GEGEN DIE DOPPELBUCHUNG ═══════════════════════════════════
 * Gebucht wird GENAU EINMAL je Einweisung. Gefragt wird an drei Stellen —
 * beim Abschliessen, nach dem Papier-Upload und ueber „Abrechnen" in der
 * Ablage (siehe darfNachbuchen); gebucht wird trotzdem nur beim ersten Mal.
 * NIE beim PDF-Export. Der SiGe-Plan bucht beim Export,
 * weil er einmal herausgeht; ein Einweisungsblatt wird dagegen mehrfach
 * gedruckt (Aushang, Ordner, Nachzuegler) — jeder weitere Druck wuerde die
 * LV-Position ein zweites Mal belasten.
 *
 * Der harte Riegel sitzt in der Datenbank: `markiereGebucht` schreibt nur,
 * solange `gebucht_am` NULL ist, und meldet mit `false` zurueck, dass schon
 * gebucht war. Deshalb laeuft er HIER ZUERST, vor der Gutschrift ins Projekt —
 * ein Lesen-dann-Schreiben haette genau das Fenster, das eine Doppelbuchung
 * braucht.
 *
 * ═══ ONLINE-TEILNAHMEN BLEIBEN IN IHRER TABELLE ═══════════════════════════
 * Die ueber den Link/QR eingetragenen Unterschriften liegen in
 * `sige_einweisung_teilnahmen` (Plan §1.1). Sie werden NIE in das jsonb
 * `teilnehmer` der Einweisung geschrieben — das automatische Speichern des
 * Editors wuerde sie beim naechsten Durchlauf ueberschreiben. PDF und Anzeige
 * bekommen eine KOPIE mit der vereinigten Liste (`vereinigeTeilnehmer`);
 * die Einweisung selbst bleibt unangetastet.
 */
export const EinweisungModul: React.FC<Props> = ({
  project,
  userProfile,
  company,
  quoteConfig,
  invoiceConfig,
  onBack,
  onUpdate,
  isReadOnly = false,
}) => {
  const isMobile = useIsMobile();
  // Preise im Abrechnungsdialog nur mit dem Recht „Preise sehen" (Admins immer).
  const rechte = useRechte(userProfile?.role);
  const meta = project.data?.metadata;
  const {
    einweisungen, onlineJeEinweisung, laden, onlineLaden, fehler, uebernehmen, onlineNeuLaden,
  } = useEinweisungen(project.id);

  const [ansicht, setAnsicht] = useState<Ansicht>('liste');
  /** Die Einweisung, die Editor oder Teilnahmen-Ansicht zeigen. */
  const [gewaehlteId, setGewaehlteId] = useState<string | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);
  const [zeigePapier, setZeigePapier] = useState(false);
  const [anlegenLaeuft, setAnlegenLaeuft] = useState(false);
  const [dokumentBusyId, setDokumentBusyId] = useState<string | null>(null);
  const [buchungFuer, setBuchungFuer] = useState<SigeEinweisung | null>(null);
  const [buchungBusyId, setBuchungBusyId] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');
  /** Die Projektablage, eingebettet ins Modul (Knopf „Dokumente"). */
  const [dokumenteOffen, setDokumenteOffen] = useState(false);
  const [ablageStand, setAblageStand] = useState<AblageStand | null>(null);

  /**
   * Riegel gegen den zweiten Fingertipp — auf dem Anlegen und auf der Buchung.
   *
   * Zustandsvariablen stehen erst nach dem naechsten Zeichnen; zwei schnelle
   * Beruehrungen laufen davor beide durch. Bei der Buchung waere die Folge ein
   * zweiter Anlauf gegen dieselbe Einweisung. Der HARTE Riegel bleibt
   * `markiereGebucht` in der Datenbank, dieser hier verhindert nur, dass es
   * ueberhaupt so weit kommt.
   */
  const anlegenRef = useRef(false);
  const buchungRef = useRef(false);
  /** Riegel gegen den zweiten Fingertipp auf „Link": zwei Anlaeufe erzeugten zwei Token. */
  const linkRef = useRef(false);

  const gewaehlte = einweisungen.find((e) => e.id === gewaehlteId) || null;
  const offene = ansicht === 'editor' ? gewaehlte : null;
  const teilnahmenFuer = ansicht === 'online' ? gewaehlte : null;

  const oeffnen = (e: SigeEinweisung) => { setGewaehlteId(e.id); setAnsicht('editor'); };
  const zeigeTeilnahmen = (e: SigeEinweisung) => { setGewaehlteId(e.id); setAnsicht('online'); };
  const zurListe = () => { setAnsicht('liste'); setGewaehlteId(null); };

  /** Online-Teilnahmen einer Einweisung — leer, wenn keine da sind. */
  const onlineVon = (e: SigeEinweisung) => onlineJeEinweisung[e.id] ?? [];

  const bueroName = (userProfile?.company_name || '').trim();
  const projektName = (project.name || meta?.projectName || '').trim();

  /**
   * Der Firmen-Briefkopf fuer das PDF.
   *
   * Genau dieser Aufruf ist Pflicht: der Generator setzt Briefpapier,
   * Kopfzeile und Spalten-Fusszeile aus dem Ergebnis von buildCompanyForPdf.
   * Wer hier die rohen Stammdaten durchreicht, bekommt ein Blatt ohne
   * hinterlegtes Briefpapier.
   */
  const companyForPdf = useMemo(
    () => buildCompanyForPdf(company, quoteConfig, invoiceConfig),
    [company, quoteConfig, invoiceConfig],
  );

  const pdfOptionen = useMemo(
    () => ({
      projektName: meta?.projectName,
      projektNummer: meta?.projectNumber,
      bauvorhabenAdresse: meta?.projectAddress,
    }),
    [meta],
  );

  /** Das ganze LV des Projekts — entscheidet, OB die Abrechnungsfrage kommt. */
  const alleServices = useMemo(() => meta?.globalData?.services || [], [meta]);

  /** Die LV-Positionen, die der Abrechnungsdialog anbietet (siehe selectBillableServices). */
  const billingServices = useMemo(() => waehleEinweisungsPositionen(alleServices), [alleServices]);

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  // ─── Anlegen ──────────────────────────────────────────────────────────────

  /**
   * Neue Einweisung: Datensatz anlegen und gleich mit dem Vorschlag aus dem
   * Plan fuellen.
   *
   * Der Planbezug (`plan_nr`, `plan_stand`, `sige_projekt_id`) wird dabei
   * EINGEFROREN — das Blatt soll spaeter sagen koennen, in welchen Planstand
   * eingewiesen wurde, auch wenn der Plan inzwischen fortgeschrieben ist.
   *
   * Schlaegt das Lesen des Plans fehl, wird trotzdem angelegt: ein leeres
   * Blatt ist bedienbar, eine Fehlermeldung statt einer Einweisung nicht.
   */
  const neueEinweisung = async () => {
    if (isReadOnly) {
      zeigeFehler('Nur-Ansicht: In diesem Projekt wird keine Einweisung angelegt.');
      return;
    }
    if (anlegenRef.current) return;
    anlegenRef.current = true;
    setAnlegenLaeuft(true);
    setMeldung(null);
    try {
      let quelle: Awaited<ReturnType<typeof ladePlanQuelle>> | null = null;
      try {
        quelle = await ladePlanQuelle(project.id);
      } catch (e) {
        console.warn('SiGe-Plan konnte für den Vorschlag nicht gelesen werden', e);
      }

      const punkte = buildEinweisungVorschlag(
        quelle?.sigeProject ?? null,
        quelle?.arbeitsschritte ?? [],
        quelle?.gefaehrdungen ?? [],
        quelle?.massnahmen ?? [],
        quelle?.bloecke ?? [],
      );

      const angelegt = await createEinweisung({
        project_id: project.id,
        sige_projekt_id: quelle?.sigeProject?.id ?? null,
        plan_nr: quelle?.sigeProject?.plan_nr ?? null,
        plan_stand: quelle?.sigeProject?.plan_datum ?? null,
        datum: heuteIso(),
        durchfuehrender_name: vorbelegterDurchfuehrender(userProfile),
        durchfuehrender_id: userProfile?.id ?? null,
        punkte,
        teilnehmer: [],
        leerzeilen: 5,
        quelle: 'digital',
      });

      uebernehmen(angelegt);
      oeffnen(angelegt);
    } catch (e) {
      zeigeFehler(describeError(e));
    } finally {
      anlegenRef.current = false;
      setAnlegenLaeuft(false);
    }
  };

  // ─── Ablage-Aktionen ──────────────────────────────────────────────────────

  const exportieren = (e: SigeEinweisung) => {
    setMeldung(null);
    // Eine KOPIE mit der vereinigten Liste (Geraet + online) geht an den
    // Generator — die Einweisung selbst bleibt, wie sie ist (siehe
    // Kopfkommentar: Online-Zeilen gehoeren nie ins jsonb).
    const mitOnline: SigeEinweisung = { ...e, teilnehmer: vereinigeTeilnehmer(e, onlineVon(e)) };
    const ergebnis = exportiereEinweisungPdf(mitOnline, companyForPdf, pdfOptionen);
    // Kein Buchen hier — siehe Kopfkommentar dieser Datei.
    if (ergebnis.status === 'fehler') zeigeFehler(ergebnis.fehler);
  };

  // ─── Projektablage („Dokumente", Stufe 1A) ────────────────────────────────

  /**
   * Legt das abgeschlossene Blatt in der Projektablage ab — automatisch beim
   * Abschliessen (Entscheid E17: Freigabe), von Hand aus der Liste fuer
   * Nachzuegler per QR. Der PDF-Export legt NICHT ab (er wird mehrfach
   * gedruckt), Entwuerfe werden nicht abgelegt.
   *
   * Wirft nie und haelt nichts auf: der Abschluss ist in der Datenbank, die
   * Abrechnungsfrage liegt bereits ueber der Liste. Scheitert die Ablage,
   * steht der Grund unter der Meldungszeile mit „Erneut ablegen".
   *
   * Welche Datei geht, entscheidet baueEinweisungAblageDatei: das gesetzte
   * Blatt mit der VEREINIGTEN Teilnehmerliste (Geraet + online), bei einer
   * Papier-Einweisung der hinterlegte Scan. Die Kennung des Standes traegt die
   * Zahl der Online-Teilnahmen — derselbe Stand wird nicht doppelt abgelegt,
   * ein Stand mit Nachzueglern ist eine neue Fassung.
   */
  /** Schickt einen fertigen Auftrag — beim ersten Mal und bei „Erneut ablegen" DENSELBEN. */
  const sendeEinweisungAuftrag = async (e: SigeEinweisung, auftrag: UnterlageAblegen, vonHand: boolean) => {
    setAblageStand({ status: 'laeuft', einweisung: e });
    const freigeben = holdIdleExit('Ablage der Einweisung läuft');
    try {
      const ergebnis = await legeUnterlageAb(auftrag);
      if (ergebnis.status === 'fehler') {
        setAblageStand({
          status: 'fehler', einweisung: e, vonHand, auftrag,
          fehler: ergebnis.fehler || 'Die Ablage hat keinen Grund genannt.',
        });
      } else {
        setAblageStand({ status: ergebnis.status, einweisung: e, titel: auftrag.titel, warnung: einweisungAblageWarnung(ergebnis) });
      }
    } finally {
      freigeben();
    }
  };

  const legeEinweisungAb = async (e: SigeEinweisung, a: { vonHand: boolean }) => {
    if (!darfEinweisungAblegen(e, isReadOnly)) {
      if (isReadOnly) zeigeFehler('Nur-Ansicht: die Einweisung wird nicht in „Dokumente" abgelegt.');
      return;
    }
    const projektId = (project.id || '').trim();
    if (!projektId || projektId === 'temp-id') {
      setAblageStand({ status: 'fehler', einweisung: e, vonHand: a.vonHand, auftrag: null, fehler: 'Das Projekt ist noch nicht gespeichert.' });
      return;
    }
    setAblageStand({ status: 'laeuft', einweisung: e });
    let auftrag: UnterlageAblegen;
    try {
      const online = onlineVon(e);
      const mitOnline: SigeEinweisung = { ...e, teilnehmer: vereinigeTeilnehmer(e, online) };
      const datei = await baueEinweisungAblageDatei(mitOnline, companyForPdf, pdfOptionen);
      if (datei.status === 'fehler') {
        setAblageStand({ status: 'fehler', einweisung: e, vonHand: a.vonHand, auftrag: null, fehler: datei.fehler });
        return;
      }
      auftrag = {
        projectId: projektId,
        modul: 'einweisung',
        datei: datei.wert.blob,
        dateiname: datei.wert.dateiname,
        titel: einweisungAblageTitel(e),
        stand: einweisungAblageStand(e, online.length),
        fassungRef: einweisungFassungRef(e, online.length),
        anlass: 'freigabe',
        beschreibung: einweisungAblageBeschreibung(e, { vonHand: a.vonHand }),
      };
    } catch (err) {
      setAblageStand({ status: 'fehler', einweisung: e, vonHand: a.vonHand, auftrag: null, fehler: describeError(err) });
      return;
    }
    await sendeEinweisungAuftrag(e, auftrag, a.vonHand);
  };

  /** „Erneut ablegen": derselbe Auftrag, wenn es ihn gibt — sonst neu bauen. */
  const ablageWiederholen = (stand: Extract<AblageStand, { status: 'fehler' }>) =>
    stand.auftrag
      ? sendeEinweisungAuftrag(stand.einweisung, stand.auftrag, stand.vonHand)
      : legeEinweisungAb(stand.einweisung, { vonHand: stand.vonHand });

  const ablageBusyId = ablageStand?.status === 'laeuft' ? ablageStand.einweisung.id : null;

  // ─── Link fuer die Online-Einweisung ──────────────────────────────────────

  /**
   * Link aktiv/aus schalten — ueber die RPC `sige_einweisung_link_setzen`.
   *
   * Die Regel „nur auf ein abgeschlossenes Blatt" sitzt in der Datenbank;
   * der Knopf in der Ablage ist im Entwurf bereits grau, die RPC ist der
   * harte Riegel. Deaktivieren loescht den Token: ein abfotografierter
   * Aushang ist danach tot, ein erneutes Aktivieren erzeugt einen NEUEN Link.
   */
  const linkSchalten = async (e: SigeEinweisung, aktiv: boolean) => {
    if (isReadOnly) {
      zeigeFehler('Nur-Ansicht: der Link wird in diesem Projekt nicht geschaltet.');
      return;
    }
    if (linkRef.current) return;
    linkRef.current = true;
    setLinkBusyId(e.id);
    setMeldung(null);
    try {
      const { linkToken, linkAktivBis } = await setzeEinweisungLink(e.id, aktiv);
      uebernehmen({ ...e, link_token: linkToken, link_aktiv_bis: linkAktivBis });
      zeigeInfo(aktiv
        ? 'Link aktiv. QR-Code und Aushang über den Knopf „QR".'
        : 'Link deaktiviert. Ein ausgehängter QR-Code ist damit ungültig; erneutes Aktivieren erzeugt einen neuen Link.');
    } catch (err) {
      zeigeFehler(describeError(err));
    } finally {
      linkRef.current = false;
      setLinkBusyId(null);
    }
  };

  const dokumentOeffnen = async (e: SigeEinweisung) => {
    if (!e.dokument_pfad) return;
    setDokumentBusyId(e.id);
    setMeldung(null);
    try {
      const url = await signedUrlFuerDokument(e.dokument_pfad);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      zeigeFehler(describeError(err));
    } finally {
      setDokumentBusyId(null);
    }
  };

  // ─── Abrechnung ───────────────────────────────────────────────────────────

  /**
   * Die Abrechnungsfrage. Einziger Weg dorthin — aus dem Abschliessen, aus dem
   * Papier-Upload und aus der Ablage („Abrechnen").
   *
   * Der dritte Einstieg ist kein Komfort: die Frage kam sonst genau einmal.
   * Wer sie wegklickte, hatte an einem abgeschlossenen Blatt keinen Weg mehr
   * zu ihr zurueck (der Abschliessen-Knopf ist dann weg), und die gehaltene
   * Einweisung fiel still aus der Abrechnung. Siehe darfNachbuchen.
   */
  const starteBuchung = (e: SigeEinweisung) => {
    if (!darfBuchungAnbieten(e, isReadOnly || !onUpdate, alleServices)) return;
    setBuchungFuer(e);
  };

  const buchungBestaetigt = async (serviceId: string | null, menge: number) => {
    const einweisung = buchungFuer;
    setBuchungFuer(null);
    if (!einweisung || !serviceId) return;          // „ohne Leistungsbuchung"
    if (isReadOnly || !onUpdate) return;            // zweite Reihe zum Riegel oben
    if (buchungRef.current) return;                 // zweiter Fingertipp

    buchungRef.current = true;
    setBuchungBusyId(einweisung.id);
    zeigeInfo('Leistung wird gebucht…');
    // Die Zweitschrift ins Buchungsjournal wird hier nur VORGEMERKT und erst
    // hinter dem try gestellt: dieses Modul speichert nicht selbst, sondern
    // reicht ueber onUpdate an die Offline-Queue weiter — geschrieben wird die
    // Vormerkung zentral nach dem bestaetigten Flush. Und sie gehoert NICHT in
    // den try-Block: dessen catch zeigt dem Anwender einen Fehler, und ein
    // Stolpern der Zweitschrift darf nie wie ein Fehlschlag der Buchung
    // aussehen.
    let vormerkung: JournalMirrorRequest | null = null;
    try {
      // 1) DER RIEGEL ZUERST. `false` heisst: war schon gebucht, es wurde
      //    NICHTS geschrieben — dann darf hier auch nichts gutgeschrieben
      //    werden. Kein Fehler, sondern der Normalfall bei einem zweiten Anlauf.
      const hatGebucht = await markiereGebucht(einweisung.id, serviceId, menge);
      if (!hatGebucht) {
        uebernehmen({ ...einweisung, gebucht_am: einweisung.gebucht_am || new Date().toISOString() });
        zeigeInfo('Diese Einweisung war bereits abgerechnet — es wurde nichts doppelt gebucht.');
        return;
      }

      // 2) Erst danach die Gutschrift ins Projekt.
      const ergebnis = rechneEinweisungsBuchung(alleServices, serviceId, menge, {
        nummer: einweisung.nummer,
        datum: einweisung.datum,
        planNr: einweisung.plan_nr ?? undefined,
        quelle: einweisung.quelle,
        performerName: einweisung.durchfuehrender_name ?? undefined,
      });

      if (ergebnis.status === 'fehler') {
        // Der Riegel steht bereits — das muss der Anwender wissen, sonst
        // sucht er die Buchung im Projekt und bucht sie von Hand ein zweites
        // Mal ein.
        zeigeFehler(
          `${ergebnis.fehler} Die Einweisung ist als abgerechnet vermerkt; ` +
          'bitte den Leistungsstand des Projekts prüfen.',
        );
        return;
      }
      if (!ergebnis.wert) {
        zeigeFehler('Die gewählte LV-Position wurde im Projekt nicht gefunden. Die Einweisung ist als abgerechnet vermerkt.');
        return;
      }

      const { services: neueServices, logEntry } = ergebnis.wert;
      const vorherigerLog = meta?.globalData?.serviceLog || [];
      onUpdate({
        ...project.data,
        metadata: {
          ...meta,
          globalData: {
            ...(meta?.globalData || { status: 'active' as any }),
            services: neueServices,
            serviceLog: [...vorherigerLog, logEntry],
          },
        },
      });

      // Die Projekt-id ist `project.id` — dieselbe, mit der oben die Ablage
      // gelesen und `markiereGebucht` geschrieben hat, also die echte Zeile in
      // `projects`. Ohne belastbare id wird NICHT vorgemerkt: eine Zeile unter
      // 'temp-id' stuende im Journal unter einem Projekt, das es nicht gibt.
      const projektId = (project.id || '').trim();
      if (projektId && projektId !== 'temp-id') {
        vormerkung = {
          projectId: projektId,
          entries: [logEntry],
          previousLog: vorherigerLog,
          source: 'einweisung',
          linkedProjectConfig: meta?.globalData?.linkedProjectConfig ?? null,
          // Die Stelle weiss es sicher: eine Einweisung ist weder eine
          // Zusatzleistung noch der Nachweis einer BEGEHUNG.
          statements: { [logEntry.id]: { isAddon: false, coversInspection: false } },
        };
      } else {
        console.warn('[EinweisungModul] Buchung ohne belastbare Projekt-id — nicht im Journal vorgemerkt.');
      }

      uebernehmen({
        ...einweisung,
        gebucht_am: new Date().toISOString(),
        gebuchte_position_id: serviceId,
        gebuchte_menge: menge,
      });
      zeigeInfo('Leistung gebucht.');
    } catch (e) {
      zeigeFehler(describeError(e));
    } finally {
      buchungRef.current = false;
      setBuchungBusyId(null);
    }
    if (vormerkung) queueJournalMirror(vormerkung);
  };

  // ─── Ansicht ──────────────────────────────────────────────────────────────

  return (
    <div data-glass-view="module-sige-einweisung" data-glass-bereich="module" className="flex flex-col h-full bg-slate-50">
      {offene ? (
        <EinweisungEditor
          einweisung={offene}
          online={onlineVon(offene)}
          projektName={meta?.projectName}
          projektNummer={meta?.projectNumber}
          bauvorhabenAdresse={pdfOptionen.bauvorhabenAdresse}
          company={companyForPdf}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onZurueck={zurListe}
          onOnlineTeilnahmen={() => zeigeTeilnahmen(offene)}
          onGespeichert={uebernehmen}
          onAbgeschlossen={(e) => {
            uebernehmen(e);
            // Zurueck in die Ablage: das Blatt ist eingefroren, im Editor
            // gibt es nichts mehr zu tun — und nur hier sind die Meldungen
            // zur Buchung ueberhaupt sichtbar. Der Abrechnungsdialog legt
            // sich unmittelbar darueber.
            zurListe();
            starteBuchung(e);
            // Das eingefrorene Blatt in die Projektablage — nicht abgewartet,
            // die Abrechnungsfrage hat Vorrang; der Stand erscheint unter der
            // Meldungszeile der Liste.
            void legeEinweisungAb(e, { vonHand: false });
          }}
        />
      ) : (
        <>
          <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
            <button
              onClick={teilnahmenFuer ? zurListe : onBack}
              className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-bold text-slate-800 truncate">
                {teilnahmenFuer ? `Online-Teilnahmen — Einweisung Nr. ${teilnahmenFuer.nummer}` : 'Einweisung SiGe-Plan'}
              </h1>
              <p className="text-xs text-slate-500 truncate">{project.name || meta?.projectName}</p>
            </div>
            {/* Dieselbe Projektablage wie im SiGe-Plan und in der Unterlage,
                gefiltert auf dieses Modul — hier liegen die abgelegten
                Blaetter. Bewusst ohne Bedingung auf die Projekt-Id: das Panel
                erkennt ein ungespeichertes Projekt selbst und sagt es. */}
            <button
              onClick={() => setDokumenteOffen(true)}
              title="Abgelegte Einweisungsblätter und weitere Dokumente der Projektablage"
              className="shrink-0 min-h-[44px] px-3 flex items-center gap-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors"
            >
              <FolderOpen className="w-4 h-4" /> Dokumente
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
            {isReadOnly && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
                Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Einweisungen lassen sich ansehen
                und drucken — anlegen, ändern, hochladen, abschließen und buchen ist gesperrt.
              </div>
            )}

            {meldung && (
              <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${
                meldungTon === 'fehler'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                {meldungTon === 'fehler' && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
                <span className="flex-1">{meldung}</span>
                <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {/* Stand der Projektablage — eigene Zeile, damit ein Ablagefehler
                nicht hinter einer Buchungsmeldung verschwindet. */}
            {ablageStand && (
              <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${
                ablageStand.status === 'fehler'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : ablageStand.status === 'schon_abgelegt' && ablageStand.warnung
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : ablageStand.status === 'laeuft'
                      ? 'bg-slate-50 border-slate-200 text-slate-600'
                      : 'bg-emerald-50 border-emerald-200 text-emerald-800'
              }`}>
                {ablageStand.status === 'laeuft' && <Loader2 className="w-4 h-4 shrink-0 mt-px animate-spin" />}
                {(ablageStand.status === 'fehler' || (ablageStand.status === 'schon_abgelegt' && ablageStand.warnung)) && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
                {(ablageStand.status === 'abgelegt' || (ablageStand.status === 'schon_abgelegt' && !ablageStand.warnung)) && <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" />}
                <span className="flex-1">
                  {ablageStand.status === 'laeuft' && `Einweisung Nr. ${ablageStand.einweisung.nummer} wird in „Dokumente" abgelegt…`}
                  {ablageStand.status === 'abgelegt' && <>In „Dokumente" abgelegt: <span className="font-semibold">{ablageStand.titel}</span></>}
                  {ablageStand.status === 'schon_abgelegt' && (
                    <>
                      Dieser Stand liegt bereits in „Dokumente": <span className="font-semibold">{ablageStand.titel}</span> — es wurde nichts doppelt abgelegt.
                      {/* Verwaiste Datei im Bucket: liegt, aber mit Rest — gelb, kein Retry. */}
                      {ablageStand.warnung && <> <span className="font-semibold">{ablageStand.warnung}</span></>}
                    </>
                  )}
                  {ablageStand.status === 'fehler' && <>Einweisung Nr. {ablageStand.einweisung.nummer} nicht in „Dokumente" abgelegt: {ablageStand.fehler} Abschluss und Buchung bleiben gültig.</>}
                </span>
                {ablageStand.status === 'fehler' && (
                  <button
                    onClick={() => void ablageWiederholen(ablageStand)}
                    disabled={isReadOnly}
                    title={isReadOnly
                      ? readOnlyTitle('Die Einweisung wird nicht in „Dokumente" abgelegt.')
                      : ablageStand.auftrag
                        ? 'Genau diese Datei erneut in „Dokumente" ablegen'
                        : 'Datei neu bauen und in „Dokumente" ablegen'}
                    className="shrink-0 px-2.5 py-1 font-bold text-red-700 bg-white border border-red-200 hover:bg-red-100 rounded-md flex items-center gap-1.5 disabled:opacity-40"
                  >
                    <RotateCw className="w-3.5 h-3.5" /> Erneut ablegen
                  </button>
                )}
                {(ablageStand.status === 'abgelegt' || ablageStand.status === 'schon_abgelegt') && (
                  <button
                    onClick={() => setDokumenteOffen(true)}
                    className="shrink-0 px-2.5 py-1 font-bold text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-100 rounded-md flex items-center gap-1.5"
                  >
                    <FolderOpen className="w-3.5 h-3.5" /> Dokumente öffnen
                  </button>
                )}
                {ablageStand.status !== 'laeuft' && (
                  <button onClick={() => setAblageStand(null)} className="p-1" title="Hinweis ausblenden"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
            )}

            {teilnahmenFuer ? (
              <OnlineTeilnahmen
                einweisung={teilnahmenFuer}
                online={onlineVon(teilnahmenFuer)}
                isMobile={isMobile}
                aktualisiert={onlineLaden}
                onAktualisieren={() => void onlineNeuLaden()}
                onZurueck={zurListe}
              />
            ) : (
              <EinweisungListe
                einweisungen={einweisungen}
                laden={laden}
                fehler={fehler}
                isReadOnly={isReadOnly}
                isMobile={isMobile}
                busy={anlegenLaeuft}
                dokumentBusyId={dokumentBusyId}
                onOeffnen={oeffnen}
                onNeu={() => void neueEinweisung()}
                onPapierUpload={() => setZeigePapier(true)}
                onPdf={exportieren}
                onDokument={(e) => void dokumentOeffnen(e)}
                buchungBusyId={buchungBusyId}
                kannAbrechnen={(e) => darfNachbuchen(e, isReadOnly || !onUpdate, alleServices)}
                onAbrechnen={starteBuchung}
                onlineJeEinweisung={onlineJeEinweisung}
                linkBusyId={linkBusyId}
                onLinkSchalten={(e, aktiv) => void linkSchalten(e, aktiv)}
                onOnlineTeilnahmen={zeigeTeilnahmen}
                kannAblegen={(e) => darfEinweisungAblegen(e, isReadOnly)}
                onAblegen={(e) => void legeEinweisungAb(e, { vonHand: true })}
                ablageBusyId={ablageBusyId}
                projektName={projektName}
                bueroName={bueroName}
              />
            )}
          </div>
        </>
      )}

      {dokumenteOffen && (
        <DokumentePanel
          projectId={project.id}
          modul="einweisung"
          userProfile={userProfile}
          isReadOnly={isReadOnly}
          onSchliessen={() => setDokumenteOffen(false)}
        />
      )}

      {zeigePapier && (
        <PapierUploadDialog
          projectId={project.id}
          vorbelegterName={vorbelegterDurchfuehrender(userProfile)}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onClose={() => setZeigePapier(false)}
          onFertig={(e) => {
            setZeigePapier(false);
            uebernehmen(e);
            // Derselbe Weg wie beim digitalen Abschliessen: die Frage kommt
            // unmittelbar, solange der Vorgang dem Anwender praesent ist.
            starteBuchung(e);
          }}
        />
      )}

      {buchungFuer && (
        <BillingSelectionModal
          isOpen
          onClose={() => setBuchungFuer(null)}
          onConfirm={(serviceId, menge) => void buchungBestaetigt(serviceId, menge)}
          services={billingServices}
          title={`Einweisung Nr. ${buchungFuer.nummer} abrechnen`}
          context="einweisung"
          memoryScope={project.id}
          canSeeFinancials={rechte.hat('preise.sehen')}
        />
      )}
    </div>
  );
};
